import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import {
	Activity,
	BarChart01,
	Briefcase02,
	Building01,
	Building07,
	CheckDone01,
	Cube01,
	Dataflow01,
	GitMerge,
	Laptop01,
	LayersThree01,
	Lock02,
	Passport,
	Settings01,
	Shield01,
	Sliders01,
	Sliders04,
	Ticket01,
	Users01,
} from "@untitledui/icons";
import { useEffect, useMemo } from "react";
import type { NavItemType } from "@/components/application/app-navigation/config";
import { AppToaster } from "@/components/application/notifications/toaster";
import { Shell } from "@/components/Organisms/Shell/Shell";
import { waitForConfig } from "@/config";
import { useAuth } from "@/contexts/AuthProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import { authQueryOptions } from "@/services/auth";
import {
	type Organization,
	OrganizationStatusCode,
	type Organizations,
	organizationQueryOptions,
} from "@/services/organization";
import { useAppActions, useCurrentOrganization } from "@/stores/app-hooks";
import {
	authStore,
	syncCurrentUserToMixpanel,
} from "@/stores/auth-store";
import {
	isPlaceholderOrgId,
	isWebUrlOrgIdInvalid,
	resolveConcreteOrgId,
	selectActiveCanViewOrganizationDetailsMemberships,
	selectOrgAdminAccessOrganizationId,
	toAdminSwitcherOrganizations,
	shouldRedirectInvalidWebOrg,
} from "@/utils/web-org-redirect";

export const Route = createFileRoute("/_authenticated/(org-app)/_orgAdmin")({
	beforeLoad: async ({ context }) => {
		// console.log("🔐 Org Admin Layout: Checking org admin permissions...");

		// Ensure config is loaded before making API calls
		await waitForConfig();

		const userInfoQuery = await context.queryClient.ensureQueryData(
			authQueryOptions.userInfo(),
		);

		// console.log("userInfoQuery", userInfoQuery);

		const viewAllOrgsQuery = await context.queryClient.ensureQueryData(
			authQueryOptions.checkAction({
				name: "ViewAllOrganizations",
				entityType: null,
				entityId: null,
			}),
		);

		// console.log("viewAllOrgsQuery", viewAllOrgsQuery);

		const orgAdminAccessOrganizationId = selectOrgAdminAccessOrganizationId(
			userInfoQuery?.organizationUserInfo ?? [],
		);

		// Trust user-info canViewOrganizationDetails (set from ViewOrganizationInfo
		// per membership). /valid-actions with entityType null only checks the
		// session org and would lock users out when that org is not the one
		// with admin rights.
		if (!viewAllOrgsQuery && !orgAdminAccessOrganizationId) {
			throw redirect({ to: "/unauthorized" });
		}
	},
	component: OrgAdminLayout,
});

function OrgAdminLayout() {
	const auth = useAuth();
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({ strict: false });
	const storedOrganization = useCurrentOrganization();

	const userInfo = useQuery(authQueryOptions.userInfo());

	// Check if user can view all organizations using query options
	const viewAllOrgsQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ViewAllOrganizations",
			entityType: null,
			entityId: null,
		}),
	);

	const orgAdminAccessOrganizationId = useMemo(
		() =>
			selectOrgAdminAccessOrganizationId(
				userInfo.data?.organizationUserInfo ?? [],
			),
		[userInfo.data?.organizationUserInfo],
	);

	const isPlatformAdmin = viewAllOrgsQuery.data ?? false;
	const isOrgAdmin = !!orgAdminAccessOrganizationId;

	// Platform admins can view all orgs, so fetch the list (only when allowed).
	const organizationList = useQuery({
		...organizationQueryOptions.all(),
		enabled: isPlatformAdmin,
	});

	const viewDetailsOrganizations: Organizations = useMemo(() => {
		const memberships = selectActiveCanViewOrganizationDetailsMemberships(
			userInfo.data?.organizationUserInfo ?? [],
		);
		const organizations = memberships
			.map((membership) => membership.organization)
			.filter(
				(org): org is NonNullable<typeof org> =>
					!!org && typeof org.id === "string",
			);
		return toAdminSwitcherOrganizations(organizations);
	}, [userInfo.data?.organizationUserInfo]);

	const allOrganizationsOption: Organization = useMemo(
		() => ({
			id: "all",
			organizationId: "all",
			name: "All Organizations",
			mfaEnabled: true,
			createdBy: null,
			createdAt: null,
			createdAtOffset: null,
			updatedBy: null,
			updatedAt: null,
			updatedAtOffset: null,
			totalUserCount: 0,
			statusCode: OrganizationStatusCode.Active,
			oAuthProviderId: null,
			oAuthProvider: null,
			status: {
				code: OrganizationStatusCode.Active,
				name: "Active",
				description: "The organization is active",
			},
		}),
		[],
	);

	const fallbackOrganization: Organization = useMemo(
		() => ({
			id: "unknown",
			organizationId: "unknown",
			name: "Organization",
			mfaEnabled: null,
			createdBy: null,
			createdAt: null,
			createdAtOffset: null,
			updatedBy: null,
			updatedAt: null,
			updatedAtOffset: null,
			totalUserCount: 0,
			statusCode: OrganizationStatusCode.Active,
			oAuthProviderId: null,
			oAuthProvider: null,
			status: {
				code: OrganizationStatusCode.Active,
				name: "Active",
				description: "The organization is active",
			},
		}),
		[],
	);

	// ViewAllOrganizations: full list. Otherwise only Active memberships that
	// can view organization details (matches $orgId guard).
	const allOrganizations: Organizations = useMemo(() => {
		if (isPlatformAdmin) {
			return [allOrganizationsOption, ...(organizationList.data ?? [])];
		}
		if (!userInfo.isSuccess) {
			return [];
		}
		return viewDetailsOrganizations;
	}, [
		isPlatformAdmin,
		userInfo.isSuccess,
		allOrganizationsOption,
		organizationList.data,
		viewDetailsOrganizations,
	]);

	const { setApp, setAvailableOrganizations, setOrganization } =
		useAppActions();
	const routeOrgId = (params as { orgId?: string }).orgId;
	const firstConcreteOrgId = allOrganizations.find(
		(org) => !isPlaceholderOrgId(org.id),
	)?.id;
	const concreteOrgId = resolveConcreteOrgId({
		urlOrgId: routeOrgId,
		storeOrgId: storedOrganization?.id,
		activeOrganizationId: authStore.getState().activeOrganizationId,
		userInfoCurrentOrganizationId: userInfo.data?.currentOrganizationId,
		firstAvailableOrgId: firstConcreteOrgId,
		availableOrganizations: allOrganizations,
	});
	const currentOrgId = routeOrgId ?? concreteOrgId ?? "unknown";

	// Find the current organization based on URL
	const currentOrganization =
		allOrganizations.find((org) => org.id === currentOrgId) ??
		allOrganizations[0] ??
		fallbackOrganization;

	useEffect(() => {
		setApp("org-admin");
		setAvailableOrganizations(allOrganizations);
		if (
			currentOrganization.id !== "unknown" &&
			allOrganizations.some((org) => org.id === currentOrganization.id)
		) {
			setOrganization(currentOrganization.id);
			if (!isPlaceholderOrgId(currentOrganization.id)) {
				authStore
					.getState()
					.setActiveOrganization(currentOrganization.id);
			} else {
				syncCurrentUserToMixpanel();
			}
		}
	}, [
		allOrganizations,
		currentOrganization.id,
		setApp,
		setAvailableOrganizations,
		setOrganization,
	]);

	// Use the same list as the switcher/access gates (canViewOrganizationDetails
	// for non-platform users), not every Active membership — otherwise a bad URL
	// redirects to an org the $orgId guard rejects.
	const firstOrgId = firstConcreteOrgId;
	const urlOrgIdInvalid = isWebUrlOrgIdInvalid(
		currentOrgId,
		allOrganizations,
	);
	const orgListReady =
		userInfo.isSuccess && viewAllOrgsQuery.isFetched && !isPlatformAdmin;

	useEffect(() => {
		if (
			!shouldRedirectInvalidWebOrg({
				isOrgListReady: orgListReady,
				firstOrgId,
				urlOrgIdInvalid,
			})
		) {
			return;
		}
		navigate({ to: `/org-admin/${firstOrgId}/home` });
	}, [orgListReady, firstOrgId, urlOrgIdInvalid, navigate]);

	// Organization Admin Navigation - use currentOrgId from URL
	const orgAdminNavigationItems: NavItemType[] = useMemo(
		() => [
			{
				label: t("admin.nav.items.home.label"),
				href: `/org-admin/${currentOrgId}/home`,
				icon: BarChart01,
			},
			{
				label: t("admin.nav.items.users.label"),
				href: `/org-admin/${currentOrgId}/users`,
				icon: Users01,
			},
			{
				label: t("admin.nav.items.roles.label"),
				href: `/org-admin/${currentOrgId}/roles`,
				icon: Passport,
			},
			{
				label: `${t("subOrganization.entityLabel")}s`,
				href: `/org-admin/${currentOrgId}/sub-organizations`,
				icon: Building01,
			},
			{
				label: t("admin.nav.items.subContractors.label"),
				href: `/org-admin/${currentOrgId}/sub-contractors`,
				icon: GitMerge,
			},
			{
				label: t("admin.nav.items.configuration.label"),
				icon: Sliders04,
				items: [
					{
						label: t("admin.nav.items.jobTypes.label"),
						href: `/org-admin/${currentOrgId}/configuration/jobTypes`,
						icon: Briefcase02,
					},
					{
						label: t("admin.nav.items.ticketTypes.label"),
						href: `/org-admin/${currentOrgId}/configuration/ticketTypes`,
						icon: Ticket01,
					},
					{
						label: t("admin.nav.items.recordTypes.label"),
						href: `/org-admin/${currentOrgId}/configuration/recordTypes`,
						icon: LayersThree01,
					},
					{
						label: t("admin.nav.items.assetTypes.label"),
						href: `/org-admin/${currentOrgId}/configuration/assetTypes`,
						icon: Cube01,
					},
					{
						label: t("admin.nav.items.lifecycles.label"),
						href: `/org-admin/${currentOrgId}/configuration/lifecycles`,
						icon: Dataflow01,
					},
				],
			},
			{
				label: t("admin.nav.items.activity.label"),
				icon: Activity,
				items: [
					{
						label: t("admin.nav.items.activity.logs.label"),
						href: `/org-admin/${currentOrgId}/activity/logs`,
						icon: CheckDone01,
					},
					{
						label: t("admin.nav.items.activity.sessions.label"),
						href: `/org-admin/${currentOrgId}/activity/sessions`,
						icon: Laptop01,
					},
				],
			},
			{
				label: t("admin.nav.items.settings.label"),
				icon: Settings01,
				items: [
					{
						label: t("admin.nav.items.settings.preferences.label"),
						href: `/org-admin/${currentOrgId}/settings/preferences`,
						icon: Sliders01,
					},
					{
						label: t("admin.nav.items.settings.security.label"),
						href: `/org-admin/${currentOrgId}/settings/security`,
						icon: Lock02,
					},
				],
			},
		],
		[currentOrgId, t],
	);
	const { src: userAvatarSrc } = useUserAvatarSrc(
		auth.user?.id || "",
		auth.user?.hasAvatar,
	);

	return (
		<>
			<Shell
				variant="brand"
				accountMode="portal"
				logoSrc="/images/AdminLogo.png"
				// Navigation
				items={orgAdminNavigationItems}
				// User Info
				name={
					auth.user?.displayName || auth.user?.firstName || t("admin.user.name")
				}
				email={auth.user?.email || ""}
				onSignOut={auth.logout}
				onUserProfile={() => {
					// Navigate with profileDrawer search param (defined in _authenticated route)
					const currentSearch = new URLSearchParams(window.location.search);
					currentSearch.set("profileDrawer", "open");
					navigate({
						to: ".",
						search: Object.fromEntries(currentSearch) as Record<string, string>,
					});
				}}
				homeUrl={`/org-admin/${currentOrgId}/home`}
				// Brand LeftNav — active/icon classes derived from variant in LeftNav
				activeBackgroundClass="bg-gray-100"
				iconClassName="text-gray-600"
				headerText={t("org-admin./.title")}
				// Organization Switcher Card
				showOrgNavCard={true}
				showOrgNavSelector={allOrganizations.length > 1}
				user1Name={
					currentOrganization?.name || t("admin.menu.selectOrganization")
				}
				userAvatarSrc={userAvatarSrc || undefined}
				avatar1Initials={currentOrganization?.name?.charAt(0) || "ORG"}
				avatar1BackgroundColor="bg-[var(--Colors-Warning-600,#DC6803)]"
				avatar1Src={Building07}
				// Application Switcher Card (footer)
				showAppNavCard={true}
				showAppNavSelector={true}
				user2Name={t("admin.menu.title")}
				avatar2Src={Shield01}
				avatar2Initials="OA"
				avatar2BackgroundColor="bg-[rgba(255,255,255,0.12)]"
				availableOrganizations={allOrganizations}
				currentOrganization={currentOrganization}
				disabled={allOrganizations.length === 1}
				isPlatformAdmin={isPlatformAdmin}
				isOrgAdmin={isOrgAdmin}
				mainAppNavCardOnClickHandler={() => {
					if (concreteOrgId) {
						setOrganization(concreteOrgId);
						authStore.getState().setActiveOrganization(concreteOrgId);
						navigate({
							to: `/web/${concreteOrgId}/home`,
						});
					}
				}}
				adminAppNavCardOnClickHandler={() => {
					if (isPlatformAdmin) {
						if (
							currentOrganization.id &&
							!isPlaceholderOrgId(currentOrganization.id)
						) {
							setOrganization(currentOrganization.id);
							authStore
								.getState()
								.setActiveOrganization(currentOrganization.id);
							navigate({
								to: `/org-admin/${currentOrganization.id}/home`,
							});
						} else {
							navigate({
								to: "/admin/home",
							});
						}
					} else if (isOrgAdmin && !isPlatformAdmin) {
						if (concreteOrgId) {
							setOrganization(concreteOrgId);
							authStore.getState().setActiveOrganization(concreteOrgId);
							navigate({
								to: `/org-admin/${concreteOrgId}/home`,
							});
						}
					}
				}}
			>
				<Outlet />
			</Shell>
			<AppToaster />
		</>
	);
}
